import React, { useEffect, useMemo, useState } from "react";
import { Tabs } from "@amzn/awsui-components-react";

export interface VersionSelectorProps<T> {
  data: T[];
  getVersion: (item: T) => number;
  renderTabLabel: (version: number, items: T[]) => React.ReactNode;
  renderTabContent: (items: T[]) => React.ReactNode;
  onVersionChange: (index: number) => void;
  initialIndex?: number;
}

export default function VersionSelector<T>(props: VersionSelectorProps<T>) {
  const {
    data,
    getVersion,
    renderTabLabel,
    renderTabContent,
    onVersionChange,
    initialIndex,
  } = props;

  const didInitializeRef = React.useRef(false);
  const { sortedVersions, versionToIndexMap, versionTabs } = useMemo(() => {
    const uniqueVersions = Array.from(new Set(data.map(getVersion))).sort(
      (a, b) => b - a
    );

    const indexMap = new Map<number, number>();
    data.forEach((item, index) => {
      const version = getVersion(item);
      if (!indexMap.has(version)) {
        indexMap.set(version, index);
      }
    });

    const tabs = uniqueVersions.map((version) => {
      const itemsForVersion = data.filter(
        (item) => getVersion(item) === version
      );

      return {
        id: `version-${version}`,
        label: renderTabLabel(version, itemsForVersion),
        content: renderTabContent(itemsForVersion),
      };
    });

    return {
      sortedVersions: uniqueVersions,
      versionToIndexMap: indexMap,
      versionTabs: tabs,
    };
  }, [data, getVersion, renderTabLabel, renderTabContent]);

  const dataRef = React.useRef(data);

  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(
    sortedVersions.length > 0 ? `version-${sortedVersions[0]}` : undefined
  );

  // When data changes significantly, reset the tab selection
  useEffect(() => {
    if (sortedVersions.length === 0) return;

    const highestVersion = sortedVersions[0];
    const dataChanged = dataRef.current !== data;

    if (dataChanged || !didInitializeRef.current) {
      let selectedIndex: number;
      let selectedVersion: number;

      if (initialIndex !== undefined && data[initialIndex]) {
        selectedIndex = initialIndex;
        selectedVersion = getVersion(data[initialIndex]);
      } else {
        selectedVersion = highestVersion;
        selectedIndex = versionToIndexMap.get(highestVersion) || 0;
      }

      setSelectedTabId(`version-${selectedVersion}`);
      onVersionChange(selectedIndex);

      if (dataChanged) {
        dataRef.current = data;
      }
      if (!didInitializeRef.current) {
        didInitializeRef.current = true;
      }
    }
  }, [data, sortedVersions, versionToIndexMap, onVersionChange, initialIndex, getVersion]);

  const handleTabChange = (detail: { activeTabId: string }) => {
    const newTabId = detail.activeTabId;
    setSelectedTabId(newTabId);

    const selectedVersion = parseInt(newTabId.split("-")[1], 10);
    const newIndex = versionToIndexMap.get(selectedVersion) || 0;
    onVersionChange(newIndex);
  };

  return (
    <Tabs
      activeTabId={selectedTabId}
      tabs={versionTabs}
      onChange={({ detail }) => handleTabChange(detail)}
    />
  );
}
